/**
 * Legal / compliance page content for İyzico merchant activation (Distance Sales Agreement,
 * Delivery & Return / Refund policy, Privacy). Kept out of languageData.tsx (like landingPages/blogPosts)
 * because these are long documents, not UI strings.
 *
 * Seller: Diyalektik İnşaat Yazılım Sanayi ve Ticaret Limited Şirketi (phone 0532 552 09 03,
 * info@shifttracker.net). Per the business, address / tax office / tax number are intentionally omitted.
 * The whole text should still be reviewed by a lawyer before go-live. The refund terms reflect the stated
 * policy: a 30-day (first month) no-questions-asked, immediate money-back guarantee.
 */

export interface LegalSection {
  heading: string;
  /** Paragraphs; rendered as separate <p> blocks. */
  body: string[];
}

export interface LegalDoc {
  title: string;
  /** <title> of the page (unique across the site, rendered via Seo). */
  metaTitle: string;
  /** <meta name="description"> of the page, in the page's language. */
  metaDescription: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
  backHome: string;
}

export interface LegalContent {
  distanceSales: LegalDoc;
  deliveryReturn: LegalDoc;
  privacy: LegalDoc;
}

/** URL slugs of a legal document: Turkish at the root, English under /en/. */
export interface LegalSlug {
  slug: string;
  enSlug: string;
}

/**
 * Turkish (root) and English (/en/...) slug of each legal document. Must stay in sync with the routes
 * in src/App.tsx, package.json reactSnap.include and public/sitemap.xml (all crawlable, prerendered).
 */
export const LEGAL_SLUGS: Record<keyof LegalContent, LegalSlug> = {
  distanceSales: { slug: "mesafeli-satis-sozlesmesi", enSlug: "distance-sales" },
  deliveryReturn: { slug: "teslimat-iade", enSlug: "delivery-return" },
  privacy: { slug: "gizlilik", enSlug: "privacy" },
};

const UPDATED_TR = "Son güncelleme: 10.07.2026";
const UPDATED_EN = "Last updated: 10.07.2026";

export const legalTr: LegalContent = {
  distanceSales: {
    title: "Mesafeli Satış Sözleşmesi",
    metaTitle: "Mesafeli Satış Sözleşmesi - Shift Tracker",
    metaDescription:
      "Shift Tracker aboneliği için Mesafeli Satış Sözleşmesi: taraflar, hizmet, bedel ve ödeme, dijital teslimat, 30 gün koşulsuz iade garantisi ve uyuşmazlıklar.",
    updated: UPDATED_TR,
    intro:
      "İşbu Mesafeli Satış Sözleşmesi, 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli " +
      "Sözleşmeler Yönetmeliği uyarınca, aşağıda bilgileri yer alan Satıcı ile Shift Tracker aboneliği " +
      "satın alan Alıcı arasında elektronik ortamda kurulmuştur.",
    sections: [
      {
        heading: "1. Taraflar ve Satıcı Bilgileri",
        body: [
          "Satıcı: Diyalektik İnşaat Yazılım Sanayi ve Ticaret Limited Şirketi, " +
            "Telefon: 0532 552 09 03, E-posta: info@shifttracker.net.",
          "Alıcı: Aboneliği satın alan, hesabında belirtilen ad, unvan ve iletişim bilgilerine sahip " +
            "gerçek/tüzel kişi.",
        ],
      },
      {
        heading: "2. Sözleşmenin Konusu",
        body: [
          "Sözleşmenin konusu, Alıcı'nın shifttracker.net üzerinden elektronik ortamda sipariş verdiği, " +
            "Shift Tracker akaryakıt istasyonu vardiya/mutabakat yazılımı aboneliğinin (dijital hizmet) " +
            "satışı ve ifasıdır.",
        ],
      },
      {
        heading: "3. Hizmet, Bedel ve Ödeme",
        body: [
          "Abonelik planları ve güncel bedelleri fiyatlandırma sayfasında yer alır. Aylık plan aylık, " +
            "yıllık plan yıllık olarak, Alıcı'nın onayı ile kayıtlı kartından iyzico ödeme altyapısı " +
            "aracılığıyla otomatik olarak tahsil edilir. Fiyatlara KDV ilgili mevzuata göre yansıtılır.",
          "Ödeme, iyzico güvenli ödeme sistemi üzerinden yapılır. Kart bilgileri Satıcı tarafından " +
            "saklanmaz; ödeme kuruluşu tarafından işlenir.",
        ],
      },
      {
        heading: "4. İfa ve Teslimat",
        body: [
          "Hizmet dijitaldir; ödemenin onaylanmasının ardından abonelik erişimi derhal aktif edilir. " +
            "Fiziksel teslimat söz konusu değildir.",
        ],
      },
      {
        heading: "5. Cayma Hakkı ve İade",
        body: [
          "Satıcı, ticari bir taahhüt olarak, ilk abonelik ayı boyunca (satın alma tarihinden itibaren " +
            "30 gün) koşulsuz iade garantisi sunar: Alıcı herhangi bir gerekçe göstermeksizin talep " +
            "ettiğinde, ödenen bedel derhal ve eksiksiz olarak, ödemenin yapıldığı karta iade edilir.",
          "Aboneliğin iptali gelecekteki yenilemeleri durdurur; iptal, hesap veya iletişim kanalları " +
            "üzerinden her zaman yapılabilir. Dijital hizmetlere ilişkin yasal cayma hükümleri saklıdır.",
        ],
      },
      {
        heading: "6. Uyuşmazlıkların Çözümü",
        body: [
          "İşbu sözleşmeden doğabilecek uyuşmazlıklarda, Ticaret Bakanlığı'nca ilan edilen değerlere " +
            "kadar Alıcı'nın yerleşim yerindeki Tüketici Hakem Heyetleri, aşan durumlarda Tüketici " +
            "Mahkemeleri yetkilidir.",
        ],
      },
    ],
    backHome: "Ana sayfaya dön",
  },
  deliveryReturn: {
    title: "Teslimat ve İade Şartları",
    metaTitle: "Teslimat ve İade Şartları - Shift Tracker",
    metaDescription:
      "Shift Tracker aboneliğinin teslimat, iade ve iptal koşulları: ödeme onayıyla anında erişim, ilk 30 gün koşulsuz para iadesi ve dilediğiniz zaman iptal.",
    updated: UPDATED_TR,
    intro:
      "Shift Tracker bir dijital abonelik hizmetidir. Aşağıda hizmetin ifası (teslimat) ile iade ve " +
      "iptal koşulları açıklanmıştır.",
    sections: [
      {
        heading: "Teslimat (Hizmetin İfası)",
        body: [
          "Hizmet elektronik ortamda sunulur. Ödeme onaylandığında abonelik erişimi anında etkinleştirilir; " +
            "fiziksel bir ürün gönderimi yapılmaz. Erişim, kayıtlı kullanıcı hesabı üzerinden sağlanır.",
        ],
      },
      {
        heading: "İade ve Para İade Garantisi",
        body: [
          "İlk abonelik ayında (satın alma tarihinden itibaren 30 gün) koşulsuz para iade garantisi " +
            "geçerlidir. Memnun kalmazsanız, herhangi bir açıklama gerekmeksizin, ödediğiniz tutarın " +
            "tamamı derhal, ödemenin yapıldığı karta iade edilir.",
          "İade talepleri info@shifttracker.net adresine iletilebilir. İade, iyzico ödeme altyapısı " +
            "üzerinden orijinal ödeme yöntemine yapılır; bankaya bağlı olarak kartınıza yansıması birkaç " +
            "iş günü sürebilir.",
        ],
      },
      {
        heading: "İptal",
        body: [
          "Aboneliğinizi dilediğiniz zaman iptal edebilirsiniz. İptal, mevcut dönem sonunda gelecekteki " +
            "otomatik yenilemeleri durdurur; mevcut dönem sonuna kadar hizmet erişiminiz devam eder.",
        ],
      },
    ],
    backHome: "Ana sayfaya dön",
  },
  privacy: {
    title: "Gizlilik Sözleşmesi",
    metaTitle: "Gizlilik Sözleşmesi - Shift Tracker",
    metaDescription:
      "Shift Tracker gizlilik sözleşmesi: işlenen kişisel veriler, kart bilgilerinin iyzico ile güvenle işlenmesi, verilerin kullanımı ve KVKK haklarınız.",
    updated: UPDATED_TR,
    intro:
      "Bu Gizlilik Sözleşmesi, Shift Tracker hizmetini kullanırken kişisel verilerinizin nasıl " +
      "işlendiğini açıklar. KVKK kapsamındaki aydınlatma metnimiz ayrıca yayımlanmıştır.",
    sections: [
      {
        heading: "Toplanan Veriler",
        body: [
          "Hesap ve iletişim bilgileri (ad, e-posta, telefon, şirket bilgileri), hizmetin çalışması için " +
            "gereken operasyonel veriler (vardiya, mutabakat verileri) ve fatura/abonelik bilgileri " +
            "işlenir.",
        ],
      },
      {
        heading: "Ödeme Verileri",
        body: [
          "Kart bilgileriniz Satıcı tarafından toplanmaz veya saklanmaz. Ödemeler, PCI-DSS uyumlu iyzico " +
            "ödeme kuruluşu tarafından işlenir; kart bilgileri iyzico nezdinde güvenli biçimde tutulur.",
        ],
      },
      {
        heading: "Verilerin Kullanımı ve Paylaşımı",
        body: [
          "Veriler yalnızca hizmetin sunulması, faturalandırma, destek ve yasal yükümlülüklerin yerine " +
            "getirilmesi amacıyla işlenir. Veriler, hizmet sağlayıcılarımız (ör. ödeme, e-posta/SMS, " +
            "barındırma) dışında üçüncü taraflarla pazarlama amacıyla paylaşılmaz.",
        ],
      },
      {
        heading: "Haklarınız ve İletişim",
        body: [
          "KVKK kapsamında verilerinize erişme, düzeltme ve silme haklarına sahipsiniz. Talepleriniz için " +
            "info@shifttracker.net adresinden bize ulaşabilirsiniz.",
        ],
      },
    ],
    backHome: "Ana sayfaya dön",
  },
};

export const legalEn: LegalContent = {
  distanceSales: {
    title: "Distance Sales Agreement",
    metaTitle: "Distance Sales Agreement - Shift Tracker",
    metaDescription:
      "Distance Sales Agreement for the Shift Tracker subscription: parties, service, price and payment, digital delivery, 30-day money-back guarantee and disputes.",
    updated: UPDATED_EN,
    intro:
      "This Distance Sales Agreement is concluded electronically between the Seller identified below and " +
      "the Buyer who purchases a Shift Tracker subscription, pursuant to Turkish Consumer Protection Law " +
      "No. 6502 and the Regulation on Distance Contracts.",
    sections: [
      {
        heading: "1. Parties and Seller Information",
        body: [
          "Seller: Diyalektik İnşaat Yazılım Sanayi ve Ticaret Limited Şirketi, " +
            "Phone: 0532 552 09 03, E-mail: info@shifttracker.net.",
          "Buyer: the natural/legal person purchasing the subscription, with the name and contact details " +
            "provided in their account.",
        ],
      },
      {
        heading: "2. Subject of the Agreement",
        body: [
          "The subject of this agreement is the sale and provision of a Shift Tracker subscription (a " +
            "digital service), the fuel-station shift/reconciliation software, ordered electronically by " +
            "the Buyer via shifttracker.net.",
        ],
      },
      {
        heading: "3. Service, Price and Payment",
        body: [
          "Subscription plans and current prices are shown on the pricing page. The monthly plan is billed " +
            "monthly and the annual plan annually; with the Buyer's consent, the amount is charged " +
            "automatically to the saved card through the iyzico payment infrastructure. VAT is applied per " +
            "applicable law.",
          "Payment is processed through the iyzico secure payment system. Card details are not stored by " +
            "the Seller; they are processed by the payment institution.",
        ],
      },
      {
        heading: "4. Provision / Delivery",
        body: [
          "The service is digital; subscription access is activated immediately upon payment approval. " +
            "There is no physical delivery.",
        ],
      },
      {
        heading: "5. Right of Withdrawal and Refund",
        body: [
          "As a commercial commitment, the Seller offers an unconditional money-back guarantee during the " +
            "first subscription month (30 days from purchase): upon the Buyer's request, without any reason " +
            "required, the amount paid is refunded in full and immediately to the card used for payment.",
          "Cancelling the subscription stops future renewals and can be done at any time via the account or " +
            "support channels. Statutory withdrawal provisions for digital services are reserved.",
        ],
      },
      {
        heading: "6. Dispute Resolution",
        body: [
          "For disputes arising from this agreement, the Consumer Arbitration Committees at the Buyer's " +
            "place of residence are competent up to the thresholds announced by the Ministry of Trade, and " +
            "the Consumer Courts above those thresholds.",
        ],
      },
    ],
    backHome: "Back to home",
  },
  deliveryReturn: {
    title: "Delivery & Return Terms",
    metaTitle: "Delivery & Return Terms - Shift Tracker",
    metaDescription:
      "Delivery, refund and cancellation terms for the Shift Tracker subscription: instant access after payment, a 30-day money-back guarantee, cancel anytime.",
    updated: UPDATED_EN,
    intro:
      "Shift Tracker is a digital subscription service. The provision (delivery) of the service and the " +
      "refund/cancellation terms are described below.",
    sections: [
      {
        heading: "Delivery (Provision of Service)",
        body: [
          "The service is delivered electronically. Subscription access is activated instantly once payment " +
            "is approved; no physical product is shipped. Access is provided through the registered user " +
            "account.",
        ],
      },
      {
        heading: "Refund & Money-Back Guarantee",
        body: [
          "An unconditional money-back guarantee applies during the first subscription month (30 days from " +
            "purchase). If you are not satisfied, the full amount you paid is refunded immediately to the " +
            "card used for payment, no explanation required.",
          "Refund requests can be sent to info@shifttracker.net. Refunds are issued to the original payment " +
            "method through the iyzico payment infrastructure; depending on your bank, it may take a few " +
            "business days to appear on your card.",
        ],
      },
      {
        heading: "Cancellation",
        body: [
          "You may cancel your subscription at any time. Cancellation stops future automatic renewals at " +
            "the end of the current period; your access continues until the end of the current period.",
        ],
      },
    ],
    backHome: "Back to home",
  },
  privacy: {
    title: "Privacy Policy",
    metaTitle: "Privacy Policy - Shift Tracker",
    metaDescription:
      "Shift Tracker privacy policy: what personal data we process, how iyzico handles card payments securely, how data is used and your rights under KVKK.",
    updated: UPDATED_EN,
    intro:
      "This Privacy Policy explains how your personal data is processed when you use Shift Tracker. Our " +
      "KVKK disclosure notice is published separately.",
    sections: [
      {
        heading: "Data We Collect",
        body: [
          "We process account and contact information (name, e-mail, phone, company details), operational " +
            "data required for the service to work (shift and reconciliation data), and billing/subscription " +
            "information.",
        ],
      },
      {
        heading: "Payment Data",
        body: [
          "Your card details are not collected or stored by the Seller. Payments are processed by the " +
            "PCI-DSS-compliant iyzico payment institution; card data is held securely by iyzico.",
        ],
      },
      {
        heading: "Use and Sharing of Data",
        body: [
          "Data is processed solely to provide the service, for billing, support and to meet legal " +
            "obligations. Data is not shared with third parties for marketing purposes, other than our " +
            "service providers (e.g. payment, e-mail/SMS, hosting).",
        ],
      },
      {
        heading: "Your Rights and Contact",
        body: [
          "Under KVKK you have the right to access, correct and delete your data. For requests, contact us " +
            "at info@shifttracker.net.",
        ],
      },
    ],
    backHome: "Back to home",
  },
};
